import classNames from "classnames/dedupe";
import React, { FC, useCallback, useMemo } from "react";
import { isReactElement, isReactComponent } from "../../../helpers/isComponent";
import { Button } from "../../ActionsSelectors/Button/Button/Button";
import SvgIcon from "../../Icons/SvgIcon";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import { DropdownContext } from "../../Utility/Dropdown/Dropdown.context";
import {
  DropdownPosition,
  DropdownAlignment,
  DropdownToggleStrategy,
} from "../../Utility/Dropdown/Dropdown.interface";
import DropdownList from "../../Utility/Dropdown/DropdownList";
import calcTooltipPos from "./calcTooltipPos";
import {
  ITooltip,
  TTooltipButtonRenderFn,
  TTooltipButtonType,
} from "./Tooltip.interface";
import TooltipTriangleContainer from "./TooltipTriange/TooltipTriangleContainer";

const Tooltip: FC<ITooltip> = ({
  classes,
  header,
  button,
  size = "default",
  position = DropdownPosition.BOTTOM,
  alignment = DropdownAlignment.MIDDLE,
  toggleStrategy = DropdownToggleStrategy.CLICK,
  buttonStyle,
  children,
  onClick,
  ...rest
}) => {
  const handleTooltipClick = useCallback(() => {
    onClick && onClick();
  }, [onClick]);

  const buttonType: TTooltipButtonType = useMemo(() => {
    if (isReactComponent(button)) {
      return "Component";
    }
    if (isReactElement(button)) {
      return "Element";
    }
    return "TTooltipButtonRenderFn";
  }, [button]);

  const ButtonComponent = button as React.ElementType;

  return (
    <Dropdown
      variant="clear"
      classes={{
        button: classes?.dropdownButton,
      }}
      position={position}
      alignment={alignment}
      dropdownButton={
        <DropdownContext.Consumer>
          {({ buttonRef, opened, currentPosition, currentAlignment }) => {
            const tooltipPos = calcTooltipPos({
              currentPosition,
              currentAlignment,
            });

            return (
              <div
                className={classNames(
                  `tooltip__button`,

                  {
                    "tooltip__button--opened": opened,
                    [`tooltip__button--${tooltipPos}`]: opened,
                  },
                  classes?.tooltipButton,
                )}
                style={buttonStyle}
              >
                {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
                <>
                  {buttonType === "Component" && <ButtonComponent />}
                  {buttonType === "Element" && button}
                  {buttonType === "TTooltipButtonRenderFn" &&
                    (button as TTooltipButtonRenderFn)({
                      buttonRef,
                      opened,
                      currentPosition,
                      currentAlignment,
                    })}
                </>
              </div>
            );
          }}
        </DropdownContext.Consumer>
      }
      toggleStrategy={toggleStrategy}
      {...rest}
    >
      <DropdownList mask={TooltipTriangleContainer}>
        {({ opened, setOpened, currentPosition }) => {
          const handleClose = () => setOpened(false);

          return (
            opened && (
              <div
                className={classNames(
                  `tooltip__content tooltip__content--size-${size} tooltip__content--margin-${currentPosition} d-block position-relative start-auto top-auto end-auto bottom-auto translate-none`,
                  {
                    "tooltip__content--extended":
                      size === "large" &&
                      ![
                        DropdownAlignment.STRETCH,
                        DropdownAlignment.FIT,
                      ].includes(alignment),
                  },
                  classes?.tooltip,
                )}
                onClick={handleTooltipClick}
              >
                <div className="d-flex flex-shrink-0 min-w-0">
                  <div className="flex-grow-1 w-100">
                    {header ? (
                      <h4 className="tooltip-header">{header}</h4>
                    ) : null}
                    {children ? (
                      <p className="text-desc m-0">{children}</p>
                    ) : null}
                  </div>
                  {toggleStrategy !== DropdownToggleStrategy.HOVER && (
                    <div>
                      <div className="tooltip__close-placeholder" />
                      <Button
                        variant="modalClose"
                        data-testid="tooltip__closeBtn"
                        onClick={handleClose}
                      >
                        <SvgIcon icon="ExitCrossIcon" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )
          );
        }}
      </DropdownList>
    </Dropdown>
  );
};

export default Tooltip;
